import { ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from "@angular/core";
import { fromEvent, merge, of, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ContainerRatingsService } from "./cbta-web/ratings/container-ratings-form/services/base/container-ratings.services";
import { GoogleAuthService } from "./services/google-api.service";
import { ETrainingIdbService } from "./shared/services/e-training-idb.service";
import { SharedService } from "./shared/services/shared.service";

@Component({
	selector: "app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
	title = "cbta-frontend";
	networkStatus: any;
	networkStatus$: Subscription = Subscription.EMPTY;
	private isSyncing = false;

	// --- NEW: SW heartbeat state ---
	private heartbeatIntervalId: any = null;
	private swReg?: ServiceWorkerRegistration;
	private readonly HEARTBEAT_MS = 10000; // 10s is plenty; keep it light
	private readonly CUSTOM_SW_SCOPE = "/et-sync/"; // Must match CustomSwRegistrarService
	private visibilityHandler = () => this.postHeartbeat();
	private focusHandler = () => this.postHeartbeat();

	constructor(
		private sharedService: SharedService,
		private googleAuth: GoogleAuthService,
		private drafts: ContainerRatingsService,
		private ngZone: NgZone,
		private eTrainingIdb: ETrainingIdbService,
	) {}

	ngOnInit(): void {
		void this.ensureETrainingDbReady();
		this.checkNetworkStatus();
		this.checkDeviceStatus();

		// Resolve BFF session first, then kickoff sync — isAuthenticated() is false until configure() finishes.
		void this.googleAuth.configure().then(() => this.drafts.kickoffGlobalSync());
		void this.startSwHeartbeat();
	}
	private async ensureETrainingDbReady(): Promise<void> {
		await this.eTrainingIdb.ensureInitialized();
	}

	ngOnDestroy(): void {
		this.networkStatus$.unsubscribe();
		this.cleanupNetworkListeners();
		this.stopSwHeartbeat();
	}

	// Store bound handlers so we can remove them on destroy
	private onlineHandler = (event: Event) => this.handleOnline(event);
	private offlineHandler = (event: Event) => this.handleOffline(event);

	private initNetworkListeners(): void {
		// Initial state
		const online = navigator.onLine;
		this.networkStatus = online;
		this.sharedService.setIsOnline(online);

		// Listen once; avoid re-registering on every call
		window.addEventListener("online", this.onlineHandler);
		window.addEventListener("offline", this.offlineHandler);
	}

	private cleanupNetworkListeners(): void {
		window.removeEventListener("online", this.onlineHandler);
		window.removeEventListener("offline", this.offlineHandler);
	}

	private async handleOnline(event: Event): Promise<void> {
		this.ngZone.run(() => {
			console.log(event);
			console.log("You are now connected to the network.");
			this.networkStatus = true;
			this.sharedService.setIsOnline(true);
			console.log("%c You are connected to the network.", "background: #222; color: #bada55");
		});

		// Prevent overlapping syncs if multiple 'online' events fire
		if (this.isSyncing) return;
		this.isSyncing = true;

		try {
			// Re-validate via GET /auth/me; skip sync when session is dead to avoid 401 blips
			const sessionValid = await this.googleAuth.ensureValidTokenOnOnline();
			void this.startSwHeartbeat();
			if (sessionValid) {
				await this.drafts.syncPendingDrafts();
			}
		} catch (err) {
			console.error("Failed to sync pending drafts after coming online:", err);
		} finally {
			this.isSyncing = false;
		}
	}

	private handleOffline(event: Event): void {
		this.ngZone.run(() => {
			console.log(event);
			this.networkStatus = false;
			this.sharedService.setIsOnline(false);
			console.log("%c You are NOT connected to the network.", "background: #222; color: #ff0000");
		});
	}

	async checkDeviceStatus(): Promise<void> {
		// Keep the same entry point but delegate to the safe listeners.
		// (No functional change to your higher-level logic.)
		this.initNetworkListeners();
	}

	checkNetworkStatus() {
		this.networkStatus$ = merge(of(null), fromEvent(window, "online"), fromEvent(window, "offline"))
			.pipe(map(() => navigator.onLine))
			.subscribe((status) => {
				this.networkStatus = status;
				this.sharedService.setIsOnline(status);
				console.log("this.networkStatus : ", this.networkStatus);
			});
	}

	// ================================
	// NEW: SW heartbeat
	// ================================

	/** Get the custom SW registration at /et-sync/ (safe guard wrapped). */
	private async getCustomSwRegistration(): Promise<ServiceWorkerRegistration | undefined> {
		if (!("serviceWorker" in navigator)) return undefined;
		try {
			const reg = await navigator.serviceWorker.getRegistration(this.CUSTOM_SW_SCOPE);
			return reg ?? undefined;
		} catch {
			return undefined;
		}
	}

	/** Start posting a small heartbeat to the custom SW so it skips its background job while app is active. */
	private async startSwHeartbeat(): Promise<void> {
		try {
			this.swReg = await this.getCustomSwRegistration();
			if (!this.swReg) return;

			const kick = () => {
				// Send API base configuration to the SW
				this.sendApiBaseToSw();
				// Start heartbeat
				this.postHeartbeat();
				if (!this.heartbeatIntervalId) {
					this.heartbeatIntervalId = setInterval(() => this.postHeartbeat(), this.HEARTBEAT_MS);
				}
				document.addEventListener("visibilitychange", this.visibilityHandler);
				window.addEventListener("focus", this.focusHandler);
			};

			if (this.swReg.active) {
				kick();
				return;
			}

			// if not active yet, wait for activation
			const candidate = this.swReg.installing || this.swReg.waiting;
			if (candidate) {
				const onChange = () => {
					if (candidate.state === "activated") {
						candidate.removeEventListener("statechange", onChange);
						kick();
					}
				};
				candidate.addEventListener("statechange", onChange);
			} else {
				// as a last resort, when controller changes we'll be good
				const onController = () => {
					navigator.serviceWorker.removeEventListener("controllerchange", onController);
					kick();
				};
				navigator.serviceWorker.addEventListener("controllerchange", onController);
			}
		} catch {
			// ignore
		}
	}

	/** Stop heartbeat and remove listeners (cleanup). */
	private stopSwHeartbeat(): void {
		try {
			if (this.heartbeatIntervalId) {
				clearInterval(this.heartbeatIntervalId);
				this.heartbeatIntervalId = null;
			}
			document.removeEventListener("visibilitychange", this.visibilityHandler);
			window.removeEventListener("focus", this.focusHandler);
		} catch {
			// ignore
		}
	}

	/** Send API base URL to the custom SW for API calls. */
	private sendApiBaseToSw(): void {
		try {
			const apiBase = (environment.apiDomain || window.location.origin).replace(/\/$/, "");
			this.swReg?.active?.postMessage({ type: "SET_API_BASE", base: apiBase });
		} catch {
			// ignore
		}
	}

	/** Post a minimal heartbeat message to the custom SW (if active). */
	private postHeartbeat(): void {
		try {
			// Only send when tab is actually visible (foreground)
			if (document.visibilityState !== "visible") return;
			this.swReg?.active?.postMessage({ type: "APP_HEARTBEAT" });
		} catch {
			// ignore
		}
	}
}
